import {
  QuickCreateApprovalStage,
  QuickCreateBrief,
  QuickCreateStage,
  QuickCreateState,
  ReadingAge,
  StructuredPlotData
} from '../types/storybook';

export const QUICK_CREATE_STAGE_ORDER: QuickCreateStage[] = [
  'brief',
  'outline',
  'cast-style',
  'draft',
  'review',
  'export'
];

export const QUICK_CREATE_STAGE_LABELS: Record<QuickCreateStage, string> = {
  brief: 'Brief',
  outline: 'Outline',
  'cast-style': 'Cast & Style',
  draft: 'Draft',
  review: 'Review',
  export: 'Export'
};

export const READING_AGE_PRESETS: Record<ReadingAge, {
  label: string;
  pageCount: number;
  canvasSize: string;
  storyTone: string;
  description: string;
}> = {
  '3-5': {
    label: 'Ages 3–5',
    pageCount: 8,
    canvasSize: '8.5x8.5',
    storyTone: 'Playful',
    description: 'Short read-aloud pages with large illustrations.'
  },
  '6-8': {
    label: 'Ages 6–8',
    pageCount: 12,
    canvasSize: '8x10',
    storyTone: 'Adventurous',
    description: 'A balanced illustrated story for early readers.'
  },
  '9-12': {
    label: 'Ages 9–12',
    pageCount: 16,
    canvasSize: '6x9',
    storyTone: 'Heartwarming',
    description: 'Longer scenes and more developed story beats.'
  }
};

export interface QuickCreateEstimate {
  tokens: number;
  seconds: number;
  missingCharacters: number;
  missingBackgrounds: number;
  illustratedPages: number;
  textOnlyPages: number;
}

export const createInitialQuickCreateState = (
  input: Partial<QuickCreateBrief> & Pick<QuickCreateBrief, 'premise'>,
  now = new Date().toISOString()
): QuickCreateState => {
  const readingAge = input.readingAge || '6-8';
  const preset = READING_AGE_PRESETS[readingAge];
  return {
    schemaVersion: 1,
    status: 'active',
    stage: 'brief',
    brief: {
      premise: input.premise,
      readingAge,
      pageCount: Math.max(1, Math.min(100, input.pageCount || preset.pageCount)),
      canvasSize: input.canvasSize || preset.canvasSize,
      storyType: input.storyType || 'Fairy Tale',
      storyTone: input.storyTone || preset.storyTone
    },
    composition: {
      mode: 'auto-book-rhythm',
      textDensity: 'balanced',
      treatment: 'paper-fade',
      pageOverrides: {}
    },
    approvals: {},
    stageRevisions: { brief: 1 },
    draft: {
      status: 'not-started',
      completedPages: 0,
      failedPages: 0,
      totalPages: Math.max(1, Math.min(100, input.pageCount || preset.pageCount))
    },
    updatedAt: now
  };
};

export const estimateQuickCreate = (
  state: QuickCreateState,
  plotData?: StructuredPlotData,
  existingCharacters: Array<{ name: string; imageUrl?: string }> = [],
  existingBackgrounds: Array<{ name: string; imageUrl?: string }> = []
): QuickCreateEstimate => {
  const generatedCharacters = new Set(existingCharacters
    .filter(character => Boolean(character.imageUrl))
    .map(character => character.name.trim().toLocaleLowerCase()));
  const generatedBackgrounds = new Set(existingBackgrounds
    .filter(background => Boolean(background.imageUrl))
    .map(background => background.name.trim().toLocaleLowerCase()));
  const plannedCharacters = plotData?.characters || Array.from({ length: 2 }, (_, index) => ({
    name: `estimated-character-${index}`,
    description: ''
  }));
  const plannedBackgrounds = plotData?.backgrounds || Array.from({ length: 3 }, (_, index) => ({
    name: `estimated-background-${index}`,
    description: ''
  }));
  const missingCharacters = plannedCharacters.filter(character => (
    !generatedCharacters.has(character.name.trim().toLocaleLowerCase())
  )).length;
  const missingBackgrounds = plannedBackgrounds.filter(background => (
    !generatedBackgrounds.has(background.name.trim().toLocaleLowerCase())
  )).length;
  const plannedPages: StructuredPlotData['pages'] = plotData?.pages || Array.from({ length: state.brief.pageCount }, () => ({
    backgroundName: 'estimated-background',
    characterNames: ['estimated-character']
  }));
  const illustratedPages = plannedPages.filter(page => (
    Boolean(page.backgroundName) || Boolean(page.characterNames?.length) || Boolean(page.scenePrompt)
  )).length;
  const textOnlyPages = Math.max(0, plannedPages.length - illustratedPages);

  // Mirrors the current product token schedule: 20 per generated asset/scene
  // plus 4 for page analysis. The estimate intentionally rounds up.
  const tokens = missingCharacters * 20
    + missingBackgrounds * 20
    + illustratedPages * 24
    + textOnlyPages * 4;
  const seconds = Math.max(
    60,
    (missingCharacters + missingBackgrounds) * 45 + illustratedPages * 75 + textOnlyPages * 15
  );
  return { tokens, seconds, missingCharacters, missingBackgrounds, illustratedPages, textOnlyPages };
};

export const approveQuickCreateStage = (
  state: QuickCreateState,
  stage: QuickCreateApprovalStage,
  nextStage: QuickCreateStage,
  now = new Date().toISOString()
): QuickCreateState => ({
  ...state,
  status: nextStage === 'review' ? 'draft-ready' : state.status,
  stage: nextStage,
  approvals: {
    ...state.approvals,
    [stage]: {
      approved: true,
      approvedAt: now,
      revision: state.stageRevisions[stage] || 1
    }
  },
  updatedAt: now
});

export const isQuickCreateStageUnlocked = (
  state: QuickCreateState,
  stage: QuickCreateStage
): boolean => {
  const targetIndex = QUICK_CREATE_STAGE_ORDER.indexOf(stage);
  const currentIndex = QUICK_CREATE_STAGE_ORDER.indexOf(state.stage);
  if (targetIndex <= currentIndex) return true;
  const previous = QUICK_CREATE_STAGE_ORDER[targetIndex - 1] as QuickCreateApprovalStage | undefined;
  return Boolean(
    previous
    && state.approvals[previous]?.approved
    && state.approvals[previous]?.revision === (state.stageRevisions[previous] || 1)
  );
};

export const reviseQuickCreateStage = (
  state: QuickCreateState,
  stage: QuickCreateApprovalStage,
  now = new Date().toISOString()
): QuickCreateState => {
  const revisedIndex = QUICK_CREATE_STAGE_ORDER.indexOf(stage);
  const approvals = Object.fromEntries(Object.entries(state.approvals).filter(([approvalStage]) => (
    QUICK_CREATE_STAGE_ORDER.indexOf(approvalStage as QuickCreateStage) < revisedIndex
  ))) as QuickCreateState['approvals'];
  return {
    ...state,
    status: 'active',
    stage,
    approvals,
    stageRevisions: {
      ...state.stageRevisions,
      [stage]: (state.stageRevisions[stage] || 1) + 1
    },
    updatedAt: now
  };
};

/**
 * A changed reference book or generated style direction changes the prose as
 * well as the art. If an outline exists, return to outline generation instead
 * of silently approving copy written with the previous direction.
 */
export const reviseQuickCreateReferenceDirection = (
  state: QuickCreateState,
  totalPages = state.outline?.pageCount || state.brief.pageCount,
  now = new Date().toISOString()
): QuickCreateState => {
  const revised = state.stage !== 'brief' || state.outline
    ? reviseQuickCreateStage(state, 'outline', now)
    : {
      ...state,
      status: 'active' as const,
      stage: 'brief' as const,
      updatedAt: now
    };
  const boundedTotalPages = Number.isInteger(totalPages) && totalPages > 0
    ? Math.min(100, totalPages)
    : revised.brief.pageCount;
  const { outline: _discardedOutline, ...withoutOutline } = revised;
  return {
    ...withoutOutline,
    draft: {
      status: 'not-started',
      completedPages: 0,
      failedPages: 0,
      totalPages: boundedTotalPages
    }
  };
};

const RETRYABLE_QUICK_CREATE_JOB_STATUSES = new Set([
  'failed',
  'review_required',
  'cancelled'
]);

/**
 * Only retry the exact immutable job snapshot persisted by this Quick Create
 * draft. A stale or unrelated latest job must start a new draft instead.
 */
export const shouldRetryQuickCreateDraftJob = (
  state: QuickCreateState,
  job?: { id: string; status: string } | null
): boolean => Boolean(
  job
  && state.draft?.jobId
  && job.id === state.draft.jobId
  && RETRYABLE_QUICK_CREATE_JOB_STATUSES.has(job.status)
);

export const withStableOutlinePageIds = (
  data: StructuredPlotData,
  createId: () => string = () => crypto.randomUUID()
): StructuredPlotData => ({
  ...data,
  pages: data.pages.map(page => ({
    ...page,
    outlinePageId: page.outlinePageId || createId()
  }))
});

export const formatQuickCreateDuration = (seconds: number): string => {
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  if (minutes < 60) return `about ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder ? `about ${hours} hr ${remainder} min` : `about ${hours} hr`;
};
